import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { UserService } from '@sparro/api/user/user.service';
import { BookmarkService } from '@sparro/app/core/api/bookmark/bookmark.service';
import { FollowerService } from '@sparro/app/core/api/follower/follower.service';
import { SnackbarService } from '@sparro/app/core/helpers/toastr/snackbar.service';
import { ToastrService } from '@sparro/app/core/helpers/toastr/toastr.service';
import { Report } from '@sparro/app/core/models/report.model';
import { User } from '@sparro/app/core/models/user.model';
import { ConfirmationDialogComponent } from '@sparro/app/shared/components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { ReportDialogComponent } from '@sparro/app/shared/components/dialogs/report-dialog/report-dialog.component';
import { ShareDialogComponent } from '@sparro/app/shared/components/dialogs/share-dialog/share-dialog.component';
import { environment } from '@sparro/env/environment';

@Component({
  selector: 'uni-publication-card-header',
  templateUrl: './publication-card-header.component.html',
  styleUrls: ['./publication-card-header.component.scss']
})
export class PublicationCardHeaderComponent implements OnInit {
  @Input() publication: any;
  @Input() self: boolean;
  @Input() showCardOptions: boolean = true;
  @Output() edit = new EventEmitter();
  @Output() delete = new EventEmitter();

  source = 'publications';
  fullName: string;
  user: User;

  icons = {
    bookmark: 'https://uni-common-assets.s3.amazonaws.com/icons/system/svg/1/addtocollection/default-48x8.svg',
    bookmark_fill: 'https://uni-common-assets.s3.amazonaws.com/icons/system/svg/1/addtocollection/collection_fill_orange_48x48.svg',
  };

  constructor(
    private userService: UserService,
    public dialog: MatDialog,
    public snackbarService: SnackbarService,
    public changeDetectorRef: ChangeDetectorRef,
    private followerService: FollowerService,
    private bookmarkService: BookmarkService,
    private toast: ToastrService,
  ) { }

  ngOnInit(): void {
    this.generateName(this.publication.author);
    this.user = this.userService.user.value;
    this.self = this.publication.author.id == this.user.id;
  }

  generateName(author: User): void {
    this.fullName = `${author.first_name} ${author.last_name}`;
    if (this.fullName.length > 30) {
      const lastSpaceIndex = this.fullName.lastIndexOf(' ', 30);
      this.fullName = this.fullName.slice(0, lastSpaceIndex) + '...';
    }
  }

  editPublication(): void {
    this.edit.emit('edit');
  }

  deletePublication(): void {
    this.delete.emit('delete');
  }

  toggleBookmark(): void {
    if (!this.user) {
      this.toast.error('To perform this action, please', 8000, { action: 'Login or Sign up' }).onAction().subscribe(
        (actionClicked) => {
          this.redirectToLogin();
        });
    } else {
      if (this.publication.bookmark) {
        this.bookmarkService.removeFromCollection(this.source, this.publication.slug, this.publication.bookmark?.id).subscribe(
          (response: any) => {
            if (response) {
              this.bookmarkService.removeBookmarks(this.publication);
              this.publication.bookmark = null;
              this.snackbarService.success('Removed from collection');
            }
          });
      } else {
        const data = {
          user: this.user.id
        };
        this.bookmarkService.addToCollection(this.source, this.publication.slug, data).subscribe(
          (response: any) => {
            this.publication.bookmark = response;
            this.snackbarService.success('Added to collection');
          },
          (err) => {
            console.log(err);
          }
        );
      }
    }
  }

  redirectToLogin(): void {
    window.location.href = `${environment.loginUrl}authentication?service=sign-in&continue=${window.location.href}`;
  }

  openShareDialog(): void {
    const dialogRef = this.dialog.open(ShareDialogComponent, {
      data: {
        sourceCopy: 'publications',
        slug: this.publication.slug,
        content: {
          title: this.publication.title,
          subtitle: this.publication.desc,
          cover_image: this.publication?.cover_image.url
        }
      }
    });
    dialogRef.afterClosed().subscribe(
      (response) => {

      });
  }

  createReport(): void {
    if (!this.user?.id) {
      window.location.href = `${environment.loginUrl}authentication?service=sign-in&continue=${window.location.href}`;
      return;
    } else {
      const dialogWidth = window.innerWidth <= 540 ? `90vw` : `527px`;
      const dialogRef = this.dialog.open(ReportDialogComponent, {
        width: dialogWidth,
        maxWidth: dialogWidth,
        data: {
          user: this.user,
          slug: this.publication.slug,
          source: this.source
        }
      });
      dialogRef.afterClosed().subscribe(
        (response: any) => {
          if (response) {
            this.publication.report = response;
            this.snackbarService.success('Report has been initiated');
          }
        });
    }
  }

  redactReport(): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '420px',
      data: {
        message: `This will withdraw the report and the action cannot be undone.
        Are you sure you want to continue?`,
        okayButtonText: 'Yes'
      }
    });
    dialogRef.afterClosed().subscribe(
      (result) => {
        if (result) {
          Report.redactReport(this.source, this.publication.slug, this.publication.report.id).subscribe(
            (response: any) => {
              if (response) {
                this.publication.report = response;
                this.snackbarService.success('Report has been redacted');
              }
            });
        }
      });
  }

  toggleFollow(): void {
    if (this.publication.is_following) {
      this.followerService.unfollow(this.source, this.publication.slug, this.publication.is_following?.id).subscribe(
        (response) => {
          if (response) {
            this.publication.is_following = null;
            this.snackbarService.success(`You have unfollowed <b>${this.publication.title}</b>`);
            this.changeDetectorRef.detectChanges();
          }
        });
    } else {
      const data = {
        follower: this.user?.id
      };
      this.followerService.follow(this.source, this.publication.slug, data).subscribe(
        (response: any) => {
          this.publication.is_following = response;
          this.snackbarService.success(`You have started following <b>${this.publication.title}</b>`);
          this.changeDetectorRef.detectChanges();
        },
        (err) => {
          console.log(err);
        });
    }
  }
}
